import { useState } from "react";
import { X, Phone } from "lucide-react";

const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Privacy Policy Content
  const PrivacyContent = () => (
    <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
      <p className="text-gray-800 font-medium">Last Updated: December 2024</p>
      
      <section>
        <h3 className="font-semibold text-gray-800 mb-2">1. Information We Collect</h3>
        <p>When you use our Study Eligibility Checker form, we collect the following information:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li>Full Name</li>
          <li>Phone Number</li>
          <li>Email Address</li>
          <li>Academic Information (Education level, Subject group, GPA, Year of passing)</li>
          <li>Study abroad preferences</li>
          <li>Any messages or voice recordings you submit</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">2. How We Use Your Information</h3>
        <p>The information you provide through our forms is used solely to:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li><strong>Provide Study Guidance:</strong> We use your academic details to determine your eligibility for studying in various countries and to offer personalized guidance.</li>
          <li><strong>Contact You:</strong> We may reach out to you via phone or email to provide study abroad consultation, answer your queries, and assist you with your application process.</li>
          <li><strong>Improve Our Services:</strong> Your data helps us understand user needs and improve our eligibility assessment system.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">3. Data Protection</h3>
        <p>We take your privacy seriously and implement appropriate security measures to protect your personal information. Your data is stored securely and is only accessible to authorized personnel.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">4. Data Sharing</h3>
        <p>We do not sell, trade, or share your personal information with third parties without your consent, except as required by law or to provide our services to you.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">5. Location Information</h3>
        <p>To improve our website and services, we may ask for your location permission through your browser. This is completely optional and you can decline without affecting your use of our services.</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li><strong>Why we collect:</strong> To understand which regions our visitors come from and improve our services for those areas</li>
          <li><strong>What we collect:</strong> General location data (city, region, country) - not your exact address</li>
          <li><strong>How we use it:</strong> For analytics and service improvement purposes only</li>
          <li><strong>Your choice:</strong> You can allow or deny this permission at any time through your browser settings</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">6. Your Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li>Request access to your personal data</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Opt-out of marketing communications</li>
          <li>Deny location permission without any impact on services</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">7. Contact Us</h3>
        <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us through our Contact page.</p>
      </section>
    </div>
  );

  // Terms & Conditions Content
  const TermsContent = () => (
    <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
      <p className="text-gray-800 font-medium">Last Updated: December 2024</p>
      
      <section>
        <h3 className="font-semibold text-gray-800 mb-2">1. Acceptance of Terms</h3>
        <p>By accessing and using CSB Pathway's website and services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our services.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">2. Services Description</h3>
        <p>CSB Pathway provides:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li>Study abroad eligibility assessment tools</li>
          <li>Educational guidance and consultation</li>
          <li>Information about studying in various countries</li>
          <li>Blog content related to international education</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">3. Eligibility Results</h3>
        <p>The eligibility results provided by our tool are for informational purposes only. Final admission decisions are made by respective universities and immigration authorities. We do not guarantee admission or visa approval.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">4. User Responsibilities</h3>
        <p>Users agree to:</p>
        <ul className="list-disc ml-5 mt-2 space-y-1">
          <li>Provide accurate and truthful information</li>
          <li>Use the service for lawful purposes only</li>
          <li>Not misuse or attempt to manipulate the eligibility system</li>
          <li>Respect intellectual property rights</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">5. Intellectual Property</h3>
        <p>All content on this website, including text, graphics, logos, and software, is the property of CSB Pathway and is protected by copyright laws. Unauthorized use is prohibited.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">6. Limitation of Liability</h3>
        <p>CSB Pathway shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of our services or reliance on information provided.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">7. Changes to Terms</h3>
        <p>We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.</p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-800 mb-2">8. Contact</h3>
        <p>For any questions regarding these Terms and Conditions, please reach out through our Contact page.</p>
      </section>
    </div>
  );

  // Modal Component
  const Modal = ({ show, onClose, title, children }) => {
    if (!show) return null;
    
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Glassy Blur Background */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-md"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Body */}
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <footer className="bg-gray-900 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left - Copyright + Phone */}
            <div className="flex flex-col items-center md:items-start gap-1">
              <p className="text-gray-400 text-sm">
                © CSB Pathway. All Rights Reserved.
              </p>
              <a
                href="tel:+8801349941320"
                data-testid="footer-phone-link"
                className="inline-flex items-center gap-1.5 text-gray-300 hover:text-green-400 text-sm transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                <span className="font-medium">01349941320</span>
              </a>
            </div>
            
            {/* Center - Links */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowPrivacy(true)}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Privacy
              </button>
              <span className="text-gray-600">|</span>
              <button 
                onClick={() => setShowTerms(true)}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms
              </button>
            </div>
            
            {/* Right - Developer Credit */}
            <p className="text-gray-400 text-sm">
              Developed by{" "}
              <a 
                href="https://virtualswift.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-red-400 hover:text-red-300 font-semibold transition-colors"
              >
                VirtualSwift
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      <Modal 
        show={showPrivacy} 
        onClose={() => setShowPrivacy(false)} 
        title="Privacy Policy"
      >
        <PrivacyContent />
      </Modal>

      {/* Terms Modal */}
      <Modal 
        show={showTerms} 
        onClose={() => setShowTerms(false)} 
        title="Terms & Conditions"
      >
        <TermsContent />
      </Modal>
    </>
  );
};

export default Footer;
